import { useMemo, useState } from 'react';
import type { BackendCapabilities, ImageSummary } from '@hub/shared';
import { api } from '../../../api/client';
import { ActionButton, Alert, EmptyRow, Loading, fmtAgo, fmtBytes, useFlash } from '../../../components/ui';
import {
  BulkBar, DOCKER_BASE, DataTable, TabToolbar, errMsg, makeBulkHandler,
  useSelection, useTabResource, type DockerCache,
} from './shared';

function refOf(img: ImageSummary): string {
  return img.repoTags[0] || img.id;
}

export function ImagesTab({ caps, cache, refreshToken, onRunImage }: {
  caps: BackendCapabilities;
  cache: DockerCache;
  refreshToken: number;
  onRunImage: (reference: string) => void;
}) {
  const { data, loading, error, reload } = useTabResource<ImageSummary[], { images: ImageSummary[] }>({
    cache, tab: 'images', path: `${DOCKER_BASE}/images`, extract: (r) => r.images, refreshToken,
  });
  const { show, node: flashNode } = useFlash();
  const [pullRef, setPullRef] = useState('');

  const list = useMemo(() => data ?? [], [data]);
  const selectableIds = useMemo(
    () => list.filter((img) => img.containersUsing === 0).map(refOf),
    [list],
  );
  const sel = useSelection(selectableIds);

  const deleteImage = (reference: string) => api.post(`${DOCKER_BASE}/images/delete`, { reference });

  const toolbar = (
    <TabToolbar onRefresh={() => { void reload(true); }} flash={flashNode}>
      <input
        className="form-input"
        style={{ width: '16rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
        placeholder="nginx:alpine"
        value={pullRef}
        onChange={(e) => setPullRef(e.target.value)}
      />
      {/* Pulls can take minutes; the button stays busy until the request resolves. */}
      <ActionButton
        className="btn-primary btn-sm"
        busyLabel="Pulling…"
        disabled={!pullRef.trim()}
        onClick={async () => {
          try {
            await api.post(`${DOCKER_BASE}/images/pull`, { reference: pullRef.trim() });
          } catch (err) {
            show(errMsg(err), 'error');
            throw err;
          }
          setPullRef('');
          await reload(true);
        }}
      >Pull</ActionButton>
      {caps.prune?.images === true && (
        <ActionButton
          danger
          className="btn-ghost btn-sm"
          busyLabel="Pruning…"
          confirm="Remove all dangling (untagged) images?"
          onClick={async () => {
            const r = await api.post<{ deleted: string[]; reclaimedBytes: number }>(
              `${DOCKER_BASE}/prune`, { target: 'images', dangling: true },
            );
            show(`Reclaimed ${fmtBytes(r.reclaimedBytes)}`, 'ok');
            await reload(true);
          }}
        >Prune unused</ActionButton>
      )}
    </TabToolbar>
  );

  if (loading && !data) return <>{toolbar}<Loading /></>;
  if (error && !data) return <>{toolbar}<Alert tone="error">{error}</Alert></>;

  return (
    <>
      {toolbar}
      <BulkBar count={sel.ids.length}>
        <ActionButton
          danger
          busyLabel="Deleting…"
          confirm={`Delete ${sel.ids.length} image(s)?`}
          onClick={makeBulkHandler(sel.ids, deleteImage, show, async () => { sel.clear(); await reload(true); })}
        >Delete</ActionButton>
      </BulkBar>

      <DataTable
        headers={[
          <input key="all" type="checkbox" checked={sel.allChecked} onChange={(e) => sel.selectAll(e.target.checked)} />,
          'Repository:Tag', 'ID', 'Size', 'Created', 'In use', 'Actions',
        ]}
      >
        {list.length === 0 && <EmptyRow colSpan={7}>No images. Pull one above.</EmptyRow>}
        {list.map((img) => {
          const reference = refOf(img);
          const inUse = img.containersUsing > 0;
          return (
            <tr key={img.id}>
              <td>
                {!inUse && (
                  <input type="checkbox" checked={sel.has(reference)} onChange={() => sel.toggle(reference)} />
                )}
              </td>
              <td className="mono" style={{ color: 'var(--ha-text-primary)' }}>
                {img.repoTags.length ? img.repoTags.join(', ') : '<none>'}
              </td>
              <td className="mono muted">{img.id.replace(/^sha256:/, '').substring(0, 12)}</td>
              <td className="muted">{fmtBytes(img.sizeBytes)}</td>
              <td className="muted">{fmtAgo(img.created)}</td>
              <td className="muted">{inUse ? img.containersUsing : '–'}</td>
              <td style={{ display: 'flex', gap: '0.25rem' }}>
                <button type="button" className="btn-ghost btn-sm" onClick={() => onRunImage(reference)}>Run</button>
                {inUse ? (
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    disabled
                    title={`In use by ${img.containersUsing} container(s)`}
                  >Delete</button>
                ) : (
                  <ActionButton
                    danger
                    busyLabel="Deleting…"
                    confirm={`Delete image ${reference}?`}
                    onClick={async () => {
                      try {
                        await deleteImage(reference);
                      } catch (err) {
                        show(errMsg(err), 'error');
                        throw err;
                      }
                      await reload(true);
                    }}
                  >Delete</ActionButton>
                )}
              </td>
            </tr>
          );
        })}
      </DataTable>
    </>
  );
}
